import {
  Button,
  Table as ChakraTable,
  Flex,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Spacer,
  StyleProps,
  TableContainer,
  TableContainerProps,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { flexRender, useReactTable } from "@tanstack/react-table";
import {
  Cell,
  ColumnDef,
  ColumnMeta,
  ExpandedState,
  Header,
  Row,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/table-core";
import React, { useCallback, useState } from "react";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { SearchAndFilterTypes } from "src/constants";
import { ariaSort } from "src/services/ariaHelpers";
import { Glossary } from "../Text/Glossary";
import { SortButton, SortType, UNSORTED } from "./SortButton";

export type ColumnDefWithTooltip<T> = ColumnDef<T> & {
  tooltip?: string;
  headerText?: string; // This is used when header is a component, while a string is needed to display the column header label for column visibility selection
};

export type TableHeaderProps = {
  count: number;
  more?: React.FC<{}>;
};

export interface TableProps<T> {
  data: T[];
  columns: ColumnDefWithTooltip<T>[];
  count?: number;
  columnVisibility?: {
    [k: string]: boolean;
  };
  viewColumnSelector?: () => JSX.Element;
  sortableColumnIds?: string[];
  onChangeSort?: (columnName: string, sortType: SortType) => void;
  createRowURL?: (row: Row<T>) => string;
  onClickRow?: (row: Row<T>) => void; // if createRowURL is set, onClickRow will not fire
  isLoading?: boolean;
  banner?: () => JSX.Element;
  hideHeader?: boolean;
  hideItemCount?: boolean;
  /**
   * defaultSortId & defaultSortType
   * This only affect the style of the column header, you'll need to sort the data source separately
   **/
  defaultSort?: { sortId: string; sortType: SortType };
  getSubRows?: (originalRow: T, index: number) => undefined | T[];
  getRowStyleProps?: (row: Row<T>) => StyleProps;
  getHeaderStyleProps?: (row: Header<T, unknown>) => StyleProps;
  getCellStyleProps?: (cell: Cell<T, unknown>) => StyleProps;
  tablePadding?: string;
  noDataMessage?: string;
  tableHeader?: React.FC<TableHeaderProps>;
  hasFooter?: boolean;
  expandAllProps?: ExpandAllProps;
  tableContainerProps?: TableContainerProps;
  additionalNodes?: React.ReactNode;
}

type ExpandAllProps = {
  expandAllSubRows?: boolean;
  hideExpandAll?: boolean;
  onToggle?: (toggle: "expand" | "collapse") => void;
};

export function Table<T>(props: TableProps<T>) {
  const {
    data,
    columns: [...columns], // Make a mutable copy.
    columnVisibility,
    viewColumnSelector,
    sortableColumnIds,
    onChangeSort,
    createRowURL,
    onClickRow,
    count = data.length,
    isLoading,
    banner,
    hideHeader,
    hideItemCount = false,
    getSubRows,
    expandAllProps,
    getRowStyleProps,
    getHeaderStyleProps,
    getCellStyleProps,
    tablePadding,
    noDataMessage = "No data",
    tableHeader: TableHeader,
    hasFooter = false,
    tableContainerProps,
    additionalNodes,
  } = props;

  const {
    expandAllSubRows = false,
    hideExpandAll = false,
    onToggle: onExpandAllToggle,
  } = expandAllProps ?? {};

  const [searchParams] = useSearchParams();
  const [sortId, setSortId] = useState<string | null>(
    props.defaultSort?.sortId ??
      searchParams.get(SearchAndFilterTypes.SortKey) ??
      null
  );
  const [sortType, setSortType] = useState<SortType>(
    props.defaultSort?.sortType ??
      (searchParams.get(SearchAndFilterTypes.SortType) as SortType) ??
      UNSORTED
  );
  const [expanded, setExpanded] = useState<ExpandedState>(
    expandAllSubRows ? true : {}
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSubRows,
    state: {
      expanded,
      ...(columnVisibility ? { columnVisibility } : {}),
    },
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
  });

  const handleChangeSort = useCallback(
    (id: string, sortType: SortType) => {
      setSortId(id);
      setSortType(sortType);
      onChangeSort && onChangeSort(id, sortType);
    },
    [onChangeSort]
  );

  const sortable = useCallback(
    (id: string): boolean => {
      return sortableColumnIds?.includes(id) || false;
    },
    [sortableColumnIds]
  );

  const showExpandAll = !hideExpandAll && getSubRows !== undefined;
  const showHeader =
    !hideItemCount ||
    viewColumnSelector ||
    showExpandAll ||
    additionalNodes !== null;

  return (
    <VStack width="100%" alignItems="left">
      {banner && banner()}
      {TableHeader ? (
        <TableHeader
          count={count}
          more={viewColumnSelector ? viewColumnSelector : undefined}
        />
      ) : showHeader ? (
        <Flex justifyContent="space-between">
          {hideItemCount ? null : (
            <Text textTransform="uppercase" fontSize="xs" fontWeight="bold">
              Items: {count}
            </Text>
          )}
          {viewColumnSelector || showExpandAll || additionalNodes !== null ? (
            <>
              <Spacer />
              <Flex gap="2">
                {showExpandAll ? (
                  <Button
                    size="xs"
                    variant="link"
                    leftIcon={
                      table.getIsAllRowsExpanded() ? (
                        <RiArrowUpSLine />
                      ) : (
                        <RiArrowDownSLine />
                      )
                    }
                    colorScheme="primary"
                    onClick={(event) => {
                      const toggle = table.getIsAllRowsExpanded()
                        ? "collapse"
                        : "expand";
                      const expandAll = table.getToggleAllRowsExpandedHandler();
                      expandAll(event);
                      if (onExpandAllToggle) onExpandAllToggle(toggle);
                    }}
                  >
                    {table.getIsAllRowsExpanded()
                      ? "Collapse all"
                      : "Expand all"}
                  </Button>
                ) : null}
                {viewColumnSelector && viewColumnSelector()}
                {additionalNodes}
              </Flex>
            </>
          ) : null}
        </Flex>
      ) : null}
      <TableContainer
        width="100%"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="0.8em"
        padding={tablePadding ?? "0.8em"}
        {...tableContainerProps}
      >
        <ChakraTable size="sm" variant="unstyled">
          {!hideHeader && (
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const headerContent = (
                      <span>
                        {sortable(header.id) ? (
                          <SortButton
                            onChange={handleChangeSort}
                            headerId={header.id}
                            headerLabel={
                              typeof header.column.columnDef.header === "string"
                                ? header.column.columnDef.header
                                : header.id
                            }
                            sort={sortId === header.id ? sortType : UNSORTED}
                          />
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )
                        )}
                      </span>
                    );
                    const tooltip = (
                      header.column.columnDef as ColumnDefWithTooltip<T>
                    ).tooltip;

                    const rowSpan = calculateRowSpan(header);
                    if (rowSpan === null) {
                      return null;
                    }

                    return (
                      <Th
                        className={getClassNameMeta(
                          header.column.columnDef.meta
                        )}
                        colSpan={header.colSpan}
                        rowSpan={rowSpan}
                        key={header.id}
                        aria-sort={
                          sortId === header.id ? ariaSort(sortType) : "none"
                        }
                        paddingY={0}
                        paddingX={sortable(header.id) ? 2 : 4}
                        position="relative"
                        height="5em"
                        width={
                          header.column.columnDef.size
                            ? `${header.column.columnDef.size}px`
                            : undefined
                        }
                        {...(getHeaderStyleProps &&
                          getHeaderStyleProps(header))}
                      >
                        <Tooltip isDisabled={!tooltip} label={tooltip} hasArrow>
                          {headerContent}
                        </Tooltip>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
          )}
          <Tbody>
            {!isLoading &&
              table.getRowModel().rows.map((row, i) => (
                <LinkBox
                  as={Tr}
                  key={row.id}
                  height="60px"
                  bg={row.depth > 0 ? "gray.50" : undefined}
                  position="static" // tr need to be position static, otherwise the td's border will not work when tr has background due to firefox issue: https://bugzilla.mozilla.org/show_bug.cgi?id=688556
                  className={row.depth > 0 ? "subrow" : undefined}
                  sx={{
                    // Selects an element that is NOT immediately followed by another element with .subrow class.
                    // This will target the last subrow
                    "&.subrow:not(:has(+ .subrow))": {
                      borderBottomWidth: "3px",
                    },
                  }}
                  _hover={{ backgroundColor: "gray.100" }}
                  {...((!hideHeader || i !== 0) && {
                    borderTop: "1px solid",
                    borderColor: "gray.200",
                  })}
                  {...(onClickRow && {
                    onClick() {
                      // Skip this trigger if it was a text selection action.
                      if (!window.getSelection()?.toString()) onClickRow(row);
                    },
                  })}
                  {...((!!onClickRow || !!createRowURL) && {
                    cursor: "pointer",
                    tabIndex: 0,
                  })}
                  {...(getRowStyleProps && getRowStyleProps(row))}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      className={getClassNameMeta(cell.column.columnDef.meta)}
                      key={cell.id}
                      position="relative"
                      {...(getRowStyleProps && getRowStyleProps(row))}
                      borderRight={
                        cell.column.id === "program"
                          ? "1px solid #E2E8F0"
                          : undefined
                      }
                      {...(getCellStyleProps && getCellStyleProps(cell))}
                    >
                      {createRowURL ? (
                        <LinkOverlay
                          as={RouterLink}
                          to={createRowURL ? createRowURL(row) : undefined}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </LinkOverlay>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      )}
                    </Td>
                  ))}
                </LinkBox>
              ))}
            {isLoading &&
              [...Array(10)].map((num, index) => (
                <Tr key={index}>
                  {table.getVisibleLeafColumns().map((_, cellIndex) => (
                    <Td key={cellIndex}>
                      <Skeleton height="20px" />
                    </Td>
                  ))}
                </Tr>
              ))}
            {!isLoading && table.getRowModel().rows.length === 0 && (
              <Tr key="no-data-text" fontSize="sm" p={2}>
                <Td>
                  <Glossary>{noDataMessage}</Glossary>
                </Td>
              </Tr>
            )}
          </Tbody>
          {hasFooter && (
            <Tfoot borderTop="1px solid" borderColor="gray.200">
              {table.getFooterGroups().map((footerGroup) => (
                <Tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <Td key={header.id} py={6}>
                      {flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tfoot>
          )}
        </ChakraTable>
      </TableContainer>
    </VStack>
  );
}

function calculateRowSpan<T, U>(header: Header<T, U>): number | null {
  const columnRelativeDepth = header.depth - header.column.depth;

  if (
    !header.isPlaceholder &&
    columnRelativeDepth > 1 &&
    header.id === header.column.id
  ) {
    return null;
  }

  let rowSpan = 1;
  if (header.isPlaceholder) {
    const leafs = header.getLeafHeaders();
    const leaf = leafs[leafs.length - 1];
    if (leaf) {
      rowSpan = leaf.depth - header.depth;
    }
  }

  return rowSpan;
}

function getClassNameMeta(meta: ColumnMeta | undefined): string | undefined {
  if (meta && "className" in meta && typeof meta.className === "string") {
    return meta.className;
  }

  return undefined;
}
